/**
 * Runtime configuration that determines API URLs based on the current environment
 */

interface RuntimeConfig {
  apiUrl: string;
  wsUrl: string;
  environment: 'development' | 'production' | 'local';
}

/**
 * Detect the current environment based on the hostname
 */
function detectEnvironment(): 'development' | 'production' | 'local' {
  if (typeof window === 'undefined') {
    return 'local';
  }

  const hostname = window.location.hostname;

  // Check for production domains (any .c1a.tomandco.uk or .s.pankowalski.pl without -dev)
  if (hostname.includes('.c1a.tomandco.uk') ||
      (hostname.includes('.s.pankowalski.pl') && !hostname.includes('-dev'))) {
    return 'production';
  } else if (hostname.includes('-dev.s.pankowalski.pl')) {
    return 'development';
  } else {
    return 'local';
  }
}

/**
 * Get runtime configuration based on environment detection
 */
export function getRuntimeConfig(): RuntimeConfig {
  const environment = detectEnvironment();

  // First try to use Vite environment variables if available
  const viteApiUrl = import.meta.env.VITE_API_URL;
  const viteWsUrl = import.meta.env.VITE_WS_URL;

  if (viteApiUrl && viteWsUrl) {
    return {
      apiUrl: viteApiUrl,
      wsUrl: viteWsUrl,
      environment
    };
  }

  // Get hostname for production URL selection
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Fallback to environment-based detection
  switch (environment) {
    case 'production':
      // Dynamically construct API URL based on current hostname
      const apiHostname = hostname.startsWith('api.') ? hostname : `api.${hostname}`;
      return {
        apiUrl: `https://${apiHostname}`,
        wsUrl: `wss://${apiHostname}`,
        environment
      };
    case 'development':
      return {
        apiUrl: 'https://api.forecast-dev.s.pankowalski.pl',
        wsUrl: 'wss://api.forecast-dev.s.pankowalski.pl',
        environment
      };
    case 'local':
    default:
      return {
        apiUrl: 'http://localhost:4000',
        wsUrl: 'ws://localhost:4000',
        environment
      };
  }
}

// Export the runtime config as a singleton
export const runtimeConfig = getRuntimeConfig();
